import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse } from '@codequality/vue-cli-base';

import { E0円非表示フラグ, E入力分類, E回収支払条件, E税区分, Eフェーズ種別, E資金繰りType } from '@/openapi/enum';
import {
  Cデータ資金繰り,
  データListItemResponse,
  資金繰りConfig,
  資金繰りMonthlyValue,
  資金繰りValue,
  資金繰り経常Config,
  資金繰り財務Config,
  資金繰り経常収支Config,
  資金繰り財務収支Config,
  データApi,
} from '@/openapi/user';

import AppFormatUtility from '@/classes/common/AppFormatUtility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import UserApiUtility from '@/classes/common/UserApiUtility';
import 決算期Utility from '@/classes/common/決算期Utility';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';
import 返済明細Service from '@/classes/service/計画/返済明細Service';

// ------------------------------------------------------------------
// type
// ------------------------------------------------------------------

/**
 * 資金繰り表(設定) 画面or帳票表示用テーブル
 */
type ConfigTable = {
  headCells: ConfigCell[];
  body: ConfigListItem[];
};

/**
 * 資金繰り表(設定) 画面or帳票表示用テーブル 行データ
 */
export type ConfigListItem = {
  cells: ConfigCell[];
  cssClass?: string;
  経常ConfigKey?: keyof 資金繰り経常Config;
  財務ConfigKey?: keyof 資金繰り財務Config;
  経常収支Config?: 資金繰り経常収支Config;
  財務収支Config?: 資金繰り財務収支Config;
};

/**
 * 資金繰り表(設定) 画面or帳票表示用テーブル 列データ
 */
type ConfigCell = {
  value?: string | number;
  rowspan?: number;
  colspan?: number;
  cssClass?: string;
  vHtml?: string;
};

/**
 * 資金繰り表 画面or帳票表示用テーブル
 */
type ValueTable = {
  headCells: ValueCell[];
  body: ValueListItem[];
};

/**
 * 資金繰り表 画面or帳票表示用テーブル 行データ
 */
type ValueListItem = {
  cells: ValueCell[];
  cssClass?: string;
  index: number;
};

/**
 * 資金繰り表 画面or帳票表示用テーブル 列データ
 */
export type ValueCell = ConfigCell & {
  data?: 資金繰りValueData;
  is月初資金残高?: boolean;
  calcValue?: number;
};

/**
 * 資金繰り表 内部処理用データ 各項目毎の計算/入力値データ
 */
export type 資金繰りValueData = {
  calculated: number;
  input?: 資金繰りValue;
  inputKey: Omit<資金繰りValue, 'value'>;
};

/**
 * 資金繰り表 内部処理用データ 各設定毎の行データ
 */
type 資金繰りListItem = {
  label: string;
  data: 資金繰りValueData[];
};

/**
 * 資金繰り表 内部処理用データ 資金繰りType毎の収支データ
 */
type 収支Value = {
  items: 資金繰りListItem[];
  sum: number[];
};

/**
 * 資金繰り表 内部処理用データ 経常収支データ
 */
type 経常収支Value = {
  営業収入: 収支Value;
  営業外収入: 収支Value;
  営業支出: 収支Value;
  営業外支出: 収支Value;
};

/**
 * 資金繰り表 内部処理用データ 財務収支データ
 */
type 財務収支Value = {
  財務収入: 収支Value;
  財務支出: 収支Value;
};

/**
 * 資金繰り表 内部処理用データ
 */
type 資金繰り予定Value = {
  yearMonths: number[];
  経常収支: 経常収支Value;
  経常収入計: number[];
  経常支出計: number[];
  経常収支計: number[];
  財務収支: 財務収支Value;
  財務収支計: number[];
  月初資金残高: number;
  月中現預金増減: number[];
};

/**
 * 資金繰り予定 内部処理用データ
 */
export type 資金繰り予定 = {
  データpk?: number;
  config: 資金繰りConfig;
  value: 資金繰り予定Value;
  開始月: number;
  configTable: ConfigTable;
  isEditing: boolean;
  valueTable: ValueTable;
};

/**
 * 収支 表示/参照データ型共通定義
 */
type 収支GroupCommon = Readonly<{
  type: E資金繰りType;
  label: string;
  head?: string;
  sum?: {
    label: string;
  };
}>;

/**
 * 経常収支 表示/参照データ型定義
 */
type 経常収支Group = 収支GroupCommon &
  Readonly<{
    configKey: keyof 資金繰り経常Config;
    valueKey: keyof 経常収支Value;
    sum?: {
      key: keyof Pick<資金繰り予定Value, '経常収入計' | '経常支出計'>; // sum.key を追加
    };
  }>;

/**
 * 財務収支 表示/参照データ型定義
 */
type 財務収支Group = 収支GroupCommon &
  Readonly<{
    configKey: keyof 資金繰り財務Config;
    valueKey: keyof 財務収支Value;
  }>;

/**
 * 経常収支 表示/参照データ定数
 */
const 経常収支Groups: 経常収支Group[] = [
  {
    type: E資金繰りType.営業収入,
    configKey: '営業収入s',
    valueKey: '営業収入',
    label: '営業収入',
    head: '経常収支',
  },
  {
    type: E資金繰りType.営業外収入,
    configKey: '営業外収入s',
    valueKey: '営業外収入',
    label: '営業外収入',
    sum: { label: '経常収入計：2', key: '経常収入計' },
  },
  {
    type: E資金繰りType.営業支出,
    configKey: '営業支出s',
    valueKey: '営業支出',
    label: '営業支出',
  },
  {
    type: E資金繰りType.営業外支出,
    configKey: '営業外支出s',
    valueKey: '営業外支出',
    label: '営業外支出',
    sum: { label: '経常支出計：3', key: '経常支出計' },
  },
];

/**
 * 財務収支 表示/参照データ定数
 */
const 財務収支Groups: 財務収支Group[] = [
  {
    type: E資金繰りType.財務収入,
    configKey: '財務収入s',
    valueKey: '財務収入',
    label: '財務収入',
    head: '財務収支',
    sum: { label: '計：5' },
  },
  {
    type: E資金繰りType.財務支出,
    configKey: '財務支出s',
    valueKey: '財務支出',
    label: '財務支出',
    sum: { label: '計：6' },
  },
];

const 収支ConfigCount = 10;

export default class 資金繰りService extends AbstractデータService<資金繰り予定> {
  public ymList: number[] = [];

  /** parseで渡された cデータ */
  public cデータ資金繰り: Cデータ資金繰り | null = null;

  public orgCデータ資金繰り: Cデータ資金繰り | null = null;

  protected serviceType = ServiceType.資金繰り;

  private plbscfService: 計画PlbscfService | null = null;

  private 返済明細Service: 返済明細Service | null = null;

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    this.ymList = 決算期Utility
      .get計画0期年月s()
      .concat(決算期Utility.get計画1期年月s())
      .map((row) => row.year * 100 + row.month);
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * 内部処理用データ解析処理
   *
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 資金繰り予定 {
    const data = this.findBy入力分類(データs);
    if (data === undefined) {
      const createEmpty経常収支Configs = (): 資金繰り経常収支Config[] => {
        const result: 資金繰り経常収支Config[] = [];
        for (let i = 0; i < 収支ConfigCount; i++) {
          result.push(this.createEmpty経常収支Config());
        }
        return result;
      };
      const createEmpty財務収支Configs = (): 資金繰り財務収支Config[] => {
        const result: 資金繰り財務収支Config[] = [];
        for (let i = 0; i < 収支ConfigCount; i++) {
          result.push(this.createEmpty財務収支Config());
        }
        return result;
      };

      const monthlyValues: 資金繰りMonthlyValue[] = [];
      for (let i = 0; i < MONTHS_OF_YEAR; i++) {
        monthlyValues.push({ 月初資金残高: i === 0 ? 0 : undefined, values: [] });
      }

      this.cデータ資金繰り = {
        monthlyValues,
        config: {
          開始月: String(this.ymList[0]),
          経常収支: {
            営業収入s: createEmpty経常収支Configs(),
            営業外収入s: createEmpty経常収支Configs(),
            営業支出s: createEmpty経常収支Configs(),
            営業外支出s: createEmpty経常収支Configs(),
          },
          財務収支: {
            財務収入s: createEmpty財務収支Configs(),
            財務支出s: createEmpty財務収支Configs(),
          },
        },
      };
    } else {
      this.cデータ資金繰り = JSON.parse(data.cデータ);
    }
    this.orgCデータ資金繰り = _.cloneDeep(this.cデータ資金繰り);

    if (this.plbscfService === null) {
      this.plbscfService =
        option?.計画PlbscfService === undefined
          ? new 計画PlbscfService(this.eフェーズ種別)
          : (option?.計画PlbscfService as 計画PlbscfService);
    }
    this.plbscfService.set勘定科目s = this.get勘定科目s;
    this.plbscfService.set部門s = this.get部門s;
    this.plbscfService.parse(データs);

    if (this.返済明細Service === null) {
      this.返済明細Service =
        option?.返済明細Service === undefined
          ? new 返済明細Service(this.eフェーズ種別)
          : (option?.返済明細Service as 返済明細Service);
    }
    this.返済明細Service.parse(データs);

    const 開始月 = Number(this.cデータ資金繰り!.config.開始月);
    const yearMonths = this.createYearMonths(開始月);
    const 資金繰りValue: 資金繰り予定Value = {
      yearMonths,
      月初資金残高: this.cデータ資金繰り!.monthlyValues[0].月初資金残高!,
      経常収支: {
        営業収入: { items: [], sum: [] },
        営業外収入: { items: [], sum: [] },
        営業支出: { items: [], sum: [] },
        営業外支出: { items: [], sum: [] },
      },
      経常収入計: [],
      経常支出計: [],
      経常収支計: [],
      財務収支: {
        財務収入: { items: [], sum: [] },
        財務支出: { items: [], sum: [] },
      },
      財務収支計: [],
      月中現預金増減: [],
    };

    const createEmpty資金繰りListItems = (type: E資金繰りType): 資金繰りListItem[] => {
      const result: 資金繰りListItem[] = [];
      for (let i = 0; i < 収支ConfigCount; i++) {
        result.push({
          label: '',
          data: this.ymList.map((ym) => ({
            calculated: 0,
            inputKey: { type: type, typeRowIndex: i, ym: ym },
          })),
        });
      }
      return result;
    };

    経常収支Groups.forEach((group) => {
      資金繰りValue.経常収支[group.valueKey].items = createEmpty資金繰りListItems(group.type);
    });
    財務収支Groups.forEach((group) => {
      資金繰りValue.財務収支[group.valueKey].items = createEmpty資金繰りListItems(group.type);
    });

    this.calculateConfigValues(資金繰りValue);

    const all資金繰りValues = this.getAll資金繰りValues();
    const setDataInput = (value: 収支Value): void =>
      value.items.forEach((item) =>
        item.data.forEach((d) => {
          const key = d.inputKey;
          const finded = all資金繰りValues.find(
            (v) => v.type === key.type && v.typeRowIndex === key.typeRowIndex && v.ym === key.ym
          );
          if (finded === undefined) return;

          d.input = finded;
          this.checkInputValue(yearMonths, d);
        })
      );

    経常収支Groups.forEach((group) => setDataInput(資金繰りValue.経常収支[group.valueKey]));
    財務収支Groups.forEach((group) => setDataInput(資金繰りValue.財務収支[group.valueKey]));

    this.calculateSums(資金繰りValue);

    const 資金繰りConfig = this.cデータ資金繰り!.config;
    return {
      データpk: data?.pk,
      config: 資金繰りConfig,
      value: 資金繰りValue,
      開始月: 開始月,
      configTable: this.createConfigTable(資金繰りConfig),
      isEditing: false,
      valueTable: this.createValueTable(資金繰りValue),
    };
  }

  /**
   * 帳票用データ解析処理
   *
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const table = this.parse(データs, option).valueTable;

    const convertValues = (cells: ValueCell[]): (string | number | null)[] => {
      const result: (string | number | null)[] = [];
      cells.forEach((cell) => {
        result.push(cell.value ?? '');

        const colspan = cell.colspan ?? 1;
        if (colspan > 1) result.push(...this.templateCellValues(colspan - 1));
      });

      return [...this.templateCellValues(16 - result.length), ...result];
    };

    const mulCell: ValueCell = {
      value: AbstractデータService.getMoneyUnitLabel(),
      colspan: 3,
    };

    return {
      templateRows: [0, ...table.body.map((item) => item.index)],
      values: [
        convertValues([mulCell].concat(table.headCells)),
        ...table.body.map((item) => convertValues(item.cells)),
      ],
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 資金繰り予定データ更新
   *
   * @param データpk
   * @param cデータ資金繰り
   * @returns
   */
  public update(データpk: number, cデータ資金繰り: Cデータ資金繰り): Promise<AxiosResponse<void | ErrorResponse>> {
    return UserApiUtility.ogApi(データApi).updateデータ(データpk, {
      c入力分類: E入力分類.資金繰り,
      c番号: 0,
      cフェーズ種別: Eフェーズ種別.計画フェーズ,
      cデータ: JSON.stringify(cデータ資金繰り),
    });
  }

  /**
   * 「科目」「回収・支払い条件」の設定値による金額の計算
   *
   * @param value
   */
  public calculateConfigValues(value: 資金繰り予定Value): void {
    const 資金繰りConfig = this.cデータ資金繰り!.config;

    経常収支Groups.forEach((経常収支Group) => {
      資金繰りConfig.経常収支[経常収支Group.configKey].forEach((config, index) => {
        const item = value.経常収支[経常収支Group.valueKey].items[index];
        item.label = config.label;

        const values: number[] = new Array(this.ymList.length).fill(0);
        const 回収支払条件 = config.回収支払条件;
        if (回収支払条件 !== undefined) {
          config.勘定科目Pks
            .filter((pk) => pk !== null)
            .forEach((pk) => {
              const finded = this.get勘定科目s.find((勘定科目) => 勘定科目.pk === pk);
              if (finded === undefined) return;

              let is課税 = false;
              switch (finded.c税区分) {
                case E税区分.課税売上:
                case E税区分.課税売上返還:
                case E税区分.課税仕入:
                case E税区分.課税仕入返還:
                  is課税 = true;
                  break;
              }

              this.ymList
                .map((ym) => this.plbscfService!.getValueBy勘定科目PkAndYm(pk, ym))
                .forEach((value, i) => (values[i] += value + (is課税 ? this.get消費税(value) : 0)));
            });

          let count = 0;
          switch (回収支払条件) {
            case E回収支払条件.回収0: // 当月回収
              // 0 のままで良い
              break;
            case E回収支払条件.回収1: // 翌月回収
              count += 1;
              break;
            case E回収支払条件.回収2: // 翌々月回収
              count += 2;
              break;
            case E回収支払条件.回収3: // 3か月後回収
              count += 3;
              break;
            case E回収支払条件.回収4: // 4か月後回収
              count += 4;
              break;
            case E回収支払条件.回収5: // 5か月後回収
              count += 5;
              break;
            case E回収支払条件.回収6: // 6か月後回収
              count += 6;
              break;
          }
          values.unshift(...new Array(count).fill(0));
        }

        item.data.forEach((data, i) => (data.calculated = values[i]));
      });
    });

    財務収支Groups.forEach((財務収支Group) => {
      資金繰りConfig.財務収支[財務収支Group.configKey].forEach((config, index) => {
        const item = value.財務収支[財務収支Group.valueKey].items[index];
        item.label = config.label;

        const 借入金条件 = config.借入金条件;
        const values =
          借入金条件 === undefined
            ? new Array(this.ymList.length).fill(0)
            : this.ymList.map((ym) => this.返済明細Service!.findMonthlyValueBy借入金条件And年月(借入金条件, ym) ?? 0);

        item.data.forEach((data, i) => (data.calculated = values[i]));
      });
    });
  }

  /**
   * 合計金額の計算
   *
   * @param value
   */
  public calculateSums(value: 資金繰り予定Value): void {
    const calcSum = (...valuesList: number[][]): number[] => {
      const result: number[] = [];
      for (let i = 0; i < this.ymList.length; i++) {
        result.push(_.sum(valuesList.map((values) => values[i])));
      }
      return result;
    };

    経常収支Groups.forEach((経常収支Group) => {
      const 経常収支Value = value.経常収支[経常収支Group.valueKey];
      経常収支Value.sum = calcSum(...経常収支Value.items.map((item) => this.getValues(item.data)));
    });

    value.経常収入計 = calcSum(value.経常収支.営業収入.sum, value.経常収支.営業外収入.sum);
    value.経常支出計 = calcSum(value.経常収支.営業支出.sum, value.経常収支.営業外支出.sum);

    value.経常収支計 = calcSum(
      value.経常収入計,
      value.経常支出計.map((v) => -v)
    );

    財務収支Groups.forEach((財務収支Group) => {
      const 財務収支Value = value.財務収支[財務収支Group.valueKey];
      財務収支Value.sum = calcSum(...財務収支Value.items.map((item) => this.getValues(item.data)));
    });

    value.財務収支計 = calcSum(
      value.財務収支.財務収入.sum,
      value.財務収支.財務支出.sum.map((v) => -v)
    );

    value.月中現預金増減 = calcSum(value.経常収支計, value.財務収支計);
  }

  /**
   * 資金繰り表(設定) 画面or帳票表示用テーブルを生成
   *
   * @param 資金繰りConfig
   * @returns
   */
  public createConfigTable(資金繰りConfig: 資金繰りConfig): ConfigTable {
    const body: ConfigListItem[] = [];

    const emptyCells = (size: number): ConfigCell[] => new Array(size).fill({ value: '', vHtml: '&nbsp;' });

    body.push({
      cells: [{ value: '月初資金残高：1', colspan: 3 }, ...emptyCells(6)],
      cssClass: 'majorHeaderRow',
    });

    経常収支Groups.forEach((group) => {
      Array.from({ length: 収支ConfigCount }).forEach((_, index) => {
        const cells: ConfigCell[] = [];
        if (index === 0) cells.push(...this.create経常GroupNameCells(group));

        const config = 資金繰りConfig.経常収支[group.configKey][index];
        cells.push({ value: config.label, cssClass: 'item3' });
        for (let i = 0; i < 5; i++) {
          cells.push({ value: this.get勘定科目名(config.勘定科目Pks[i]), cssClass: 'kamoku' });
        }
        cells.push({ value: config.回収支払条件 ?? '', cssClass: 'joken' });
        body.push({ cells, 経常ConfigKey: group.configKey, 経常収支Config: config });
      });

      body.push({
        cells: [{ value: '小計' }, ...emptyCells(6)],
        cssClass: 'headerRow',
      });

      if (group.sum !== undefined) {
        body.push({
          cells: [{ value: group.sum.label, colspan: 2 }, ...emptyCells(6)],
          cssClass: 'majorHeaderRow',
        });
      }
    });
    body.push({
      cells: [{ value: '経常収支：4＝2－3', colspan: 3 }, ...emptyCells(6)],
      cssClass: 'majorHeaderRow',
    });

    財務収支Groups.forEach((group) => {
      Array.from({ length: 収支ConfigCount }).forEach((_, index) => {
        const cells: ConfigCell[] = [];
        if (index === 0) cells.push(...this.create財務GroupNameCells(group));

        const config = 資金繰りConfig.財務収支[group.configKey][index];
        cells.push({ value: config.label });
        cells.push(...new Array(5).fill({ value: '', cssClass: 'grey' }));
        cells.push({ value: config.借入金条件 ?? '' });
        body.push({ cells, 財務ConfigKey: group.configKey, 財務収支Config: config });
      });

      if (group.sum !== undefined) {
        body.push({
          cells: [{ value: group.sum.label }, ...emptyCells(6)],
          cssClass: 'headerRow',
        });
      }
    });
    body.push({
      cells: [{ value: '財務収支：7＝5－6', colspan: 3 }, ...emptyCells(6)],
      cssClass: 'headerRow',
    });

    body.push(
      {
        cells: [{ value: '月中現預金増減：8＝4＋7', colspan: 3 }, ...emptyCells(6)],
        cssClass: 'majorHeaderRow',
      },
      {
        cells: [{ value: '月末資金残高（1＋8）', colspan: 3 }, ...emptyCells(6)],
        cssClass: 'total',
      }
    );

    return {
      headCells: [
        { value: '科目設定1' },
        { value: '科目設定2' },
        { value: '科目設定3' },
        { value: '科目設定4' },
        { value: '科目設定5' },
        { value: '回収・支払条件' },
      ],
      body,
    };
  }

  /**
   * 資金繰り表 画面or帳票表示用テーブルを生成
   *
   * @param 資金繰りValue
   * @returns
   */
  public createValueTable(資金繰りValue: 資金繰り予定Value): ValueTable {
    const body: ValueListItem[] = [];

    const formattedCells = (values: number[]): ValueCell[] =>
      values.map((value) => ({
        value: this.getMoneyUnitValue(value),
        cssClass: 'term',
        calcValue: value,
      }));

    const indexFrom = this.ymList.indexOf(資金繰りValue.yearMonths[0]);
    const indexTo = indexFrom + 資金繰りValue.yearMonths.length;
    const 月末資金残高s: number[] = [];
    資金繰りValue.月中現預金増減.slice(indexFrom, indexTo).forEach((value, index) => {
      const 月初資金残高 = index === 0 ? 資金繰りValue.月初資金残高 : 月末資金残高s[index - 1];
      月末資金残高s[index] = 月初資金残高 + value;
    });

    body.push({
      cells: [
        { value: '月初資金残高：1', colspan: 3 },
        {
          value: this.getMoneyUnitValue(資金繰りValue.月初資金残高),
          cssClass: 'term',
          is月初資金残高: true,
        },
        ...formattedCells(月末資金残高s.slice(0, 月末資金残高s.length - 1)),
        { value: '' },
      ],
      cssClass: 'majorHeaderRow',
      index: 1,
    });

    const is0円非表示 = AbstractデータService.計画書.c計画0円非表示フラグ === E0円非表示フラグ.非表示;
    const addRows = (
      収支Value: 収支Value,
      sumLabel: string | undefined,
      group: 経常収支Group | 財務収支Group,
      収支Header: ConfigCell
    ): void => {
      const groupHeader = this.createGroupHeader(group.label);

      const items: ValueListItem[] = Array.from({ length: 収支ConfigCount })
        .map((_, index) => {
          const item = 収支Value.items[index];
          return { label: item.label, data: item.data.slice(indexFrom, indexTo) };
        })
        .filter((item) => !is0円非表示 || this.getValues(item.data).some((value) => value !== 0))
        .map((item) => {
          const sum = _.sum(this.getValues(item.data));
          return {
            cells: [
              { value: item.label, cssClass: 'item3' },
              ...item.data.map((data) => ({
                value: this.getMoneyUnitValue(this.getValue(data)),
                cssClass: 'term',
                data: data,
              })),
              { value: this.getMoneyUnitValue(sum), cssClass: 'term', calcValue: sum },
            ],
            index: 2,
          };
        });

      if (sumLabel !== undefined) items.push(createSumRow(収支Value.sum, sumLabel, 'headerRow', 3));

      groupHeader.rowspan = items.length;
      収支Header.rowspan! += groupHeader.rowspan;

      items.forEach((item, index) => {
        if (index === 0) {
          item.cells.unshift(groupHeader);
          if (group.head !== undefined) item.cells.unshift(収支Header);
        }

        body.push(item);
      });
    };

    const createSumRow = (
      values: number[],
      label: string,
      cssClass: string,
      index: number,
      colspan?: number
    ): ValueListItem => {
      const sliced = values.slice(indexFrom, indexTo);
      const sum = _.sum(sliced);

      return {
        cells: [
          { value: label, colspan: colspan },
          ...formattedCells(sliced),
          { value: this.getMoneyUnitValue(sum), cssClass: 'term', calcValue: sum },
        ],
        cssClass: cssClass,
        index,
      };
    };

    const 経常収支Header = this.create収支Header(経常収支Groups[0].head!, 0, 'item1 white');
    経常収支Groups.forEach((group) => {
      addRows(資金繰りValue.経常収支[group.valueKey], '小計', group, 経常収支Header);

      const sum = group.sum;
      if (sum === undefined) return;

      経常収支Header.rowspan!++;
      body.push(createSumRow(資金繰りValue[sum.key], sum.label, 'majorHeaderRow', 4, 2));
    });
    body.push(createSumRow(資金繰りValue.経常収支計, '経常収支：4＝2－3', 'majorHeaderRow', 6, 3));

    const 財務収支Header = this.create収支Header(財務収支Groups[0].head!, 0, 'white');
    財務収支Groups.forEach((group) =>
      addRows(資金繰りValue.財務収支[group.valueKey], group.sum?.label, group, 財務収支Header)
    );
    body.push(createSumRow(資金繰りValue.財務収支計, '財務収支：7＝5－6', 'headerRow', 5, 3));

    body.push(createSumRow(資金繰りValue.月中現預金増減, '月中現預金増減：8＝4＋7', 'majorHeaderRow', 6, 3));
    body.push({
      cells: [{ value: '月末資金残高（1＋8）', colspan: 3 }, ...formattedCells(月末資金残高s), { value: '' }],
      cssClass: 'total',
      index: 7,
    });

    return {
      headCells: [
        ...資金繰りValue.yearMonths.map((ym) => ({ value: AppFormatUtility.format年月(ym) })),
        { value: '合計' },
      ],
      body,
    };
  }

  /**
   * 勘定科目名を取得
   *
   * @param pk
   * @returns
   */
  public get勘定科目名(pk: number | undefined): string {
    return this.勘定科目s.find((勘定科目) => 勘定科目.pk === pk)?.c勘定科目 ?? '';
  }

  /**
   * 空の経常収支設定を生成
   * 初期データ生成時、及び設定のクリア時に利用
   *
   * @returns
   */
  public createEmpty経常収支Config(): 資金繰り経常収支Config {
    return { label: '', 勘定科目Pks: [], 回収支払条件: undefined };
  }

  /**
   * 空の財務収支設定を生成
   * 初期データ生成時、及び設定のクリア時に利用
   *
   * @returns
   */
  public createEmpty財務収支Config(): 資金繰り財務収支Config {
    return { label: '', 借入金条件: undefined };
  }

  /**
   * 値の取得
   * 入力値があれば入力値、無ければ計算値を取得
   *
   * @param data
   * @returns
   */
  public getValue(data: 資金繰りValueData): number {
    return data.input?.value ?? data.calculated;
  }

  /**
   * 入力値のチェック
   * 計算値と同じ値であれば、登録用データから削除
   *
   * @param yearMonths
   * @param data
   * @returns
   */
  public checkInputValue(yearMonths: number[], data: 資金繰りValueData): void {
    const input = data.input!;
    if (input.value !== data.calculated) return;

    const monthlyValue = this.cデータ資金繰り!.monthlyValues[yearMonths.indexOf(input.ym)];
    const findIndex = monthlyValue.values.findIndex(
      (v) => v.type === input.type && v.typeRowIndex === input.typeRowIndex && v.ym === input.ym
    );
    monthlyValue.values.splice(findIndex, 1);

    data.input = undefined;
  }

  /**
   * 開始月の更新
   * 年月表示範囲の変更、及び登録用データの更新
   *
   * @param 資金繰り予定
   */
  public update開始月(資金繰り予定: 資金繰り予定): void {
    const yearMonths = this.createYearMonths(資金繰り予定.開始月);
    資金繰り予定.value.yearMonths = yearMonths;

    const all資金繰りValues = this.getAll資金繰りValues();
    this.cデータ資金繰り!.monthlyValues = yearMonths.map((ym, index) => ({
      月初資金残高: index === 0 ? 資金繰り予定.value.月初資金残高 : undefined,
      values: all資金繰りValues.filter((value) => value.ym === ym),
    }));

    const updateDataInput = (value: 収支Value): void =>
      value.items.forEach((item) =>
        item.data.forEach((d) => {
          const key = d.inputKey;
          d.input = all資金繰りValues.find(
            (v) => v.type === key.type && v.typeRowIndex === key.typeRowIndex && v.ym === key.ym
          );
        })
      );

    経常収支Groups.forEach((group) => updateDataInput(資金繰り予定.value.経常収支[group.valueKey]));
    財務収支Groups.forEach((group) => updateDataInput(資金繰り予定.value.財務収支[group.valueKey]));
  }

  /**
   * 全ての入力値の削除
   * 「再計算」ボタン押下時の利用を想定
   *
   * @param value
   */
  public deleteAllInput(value: 資金繰り予定Value): void {
    this.cデータ資金繰り!.monthlyValues.forEach((mv) => (mv.values = []));

    const deleteItemDataInput = (items: 資金繰りListItem[]): void =>
      items.forEach((item) => item.data.forEach((data) => (data.input = undefined)));

    経常収支Groups.forEach((group) => deleteItemDataInput(value.経常収支[group.valueKey].items));
    財務収支Groups.forEach((group) => deleteItemDataInput(value.財務収支[group.valueKey].items));
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 年月の一覧を生成
   *
   * @param 開始月
   * @returns
   */
  private createYearMonths(開始月: number): number[] {
    const index = this.ymList.indexOf(開始月);
    return this.ymList.slice(index, index + MONTHS_OF_YEAR);
  }

  /**
   * 全ての資金繰り入力値を取得
   *
   * @returns
   */
  private getAll資金繰りValues(): 資金繰りValue[] {
    return this.cデータ資金繰り!.monthlyValues.flatMap((mv) => mv.values);
  }

  /**
   * 値の一覧を取得
   *
   * @param data
   * @returns
   */
  private getValues(data: 資金繰りValueData[]): number[] {
    return data.map((row) => this.getValue(row));
  }

  /**
   * 経常収支グループ名セルを生成
   *
   * @param group
   * @returns
   */
  private create経常GroupNameCells(group: 経常収支Group): ConfigCell[] {
    const cell = this.createGroupHeader(group.label);

    const head = group.head;
    return head === undefined ? [cell] : [this.create収支Header(head, 46, 'item1 white'), cell];
  }

  /**
   * 財務収支グループ名セルを生成
   *
   * @param group
   * @returns
   */
  private create財務GroupNameCells(group: 財務収支Group): ConfigCell[] {
    const cell = this.createGroupHeader(group.label);

    const head = group.head;
    return head === undefined ? [cell] : [this.create収支Header(head, 22, 'white'), cell];
  }

  /**
   * 経常収支ヘッダを生成
   *
   * @param value
   * @param rowspan
   * @param cssClass
   * @returns
   */
  private create収支Header(value: string, rowspan: number, cssClass: string): ConfigCell {
    return { value, rowspan, cssClass, vHtml: [...value].join('<br />') };
  }

  /**
   * グループヘッダを生成
   *
   * @param value
   * @returns
   */
  private createGroupHeader(value: string): ConfigCell {
    return { value, rowspan: 11, cssClass: 'item2 white' };
  }
}
